'use strict';
/* ==========================================================================
   levels/title.js — the title screen, NOTEBOOK style. On the page's graph paper: a coin drawn in ink, mid-flip (it
   turns edge-on and back, H / T showing by which face is towards us, a pencil arc of its flight under it) above a tally
   row that writes itself on: H H H H H (the five heads of the video's first scene, s1), with a pencil tally (four
   strokes and the gate) and Jay's red-pen "?" (is tails due?). Jay rises in from below on the right. The title is
   handwritten with a highlighter swipe on its last word ("Due!", NB.title from lesson.json), ▶ Play → the map.
   Pure in time: everything keyed to T and the scene's entry time.
   On a phone held upright the controls move to the portrait dock (Dock.title) and the stage keeps only the art.
   Levels.title.loading() draws the loading page while the boot tier loads.
   Helpers other scenes may reuse: Levels.title.coin(x, y, r, o) (an ink coin: o.face 'H' | 'T', o.turn = the flip
   angle in radians: 0 = face-on, π/2 = edge-on).
   ========================================================================== */
(function () {
  const PLAY = { x: 1400, y: 820 };
  const HANDF = NB.FONT.hand;

  /* the title in one or two lines, each sized to fit maxW logical px */
  function titleLines(text, maxW, max) {
    const width = (s, size) => { g.save(); g.font = `700 ${size}px ${HANDF}`; const w = g.measureText(s).width; g.restore(); return w; };
    const words = String(text).split(/\s+/).filter(Boolean);
    let lines = [words.join(' ')];
    if (width(lines[0], max) > maxW && words.length > 1) {
      let best = null;
      for (let k = 1; k < words.length; k++) {
        const a = words.slice(0, k).join(' '), b = words.slice(k).join(' '), d = Math.abs(width(a, 100) - width(b, 100));
        if (!best || d < best.d) best = { d, lines: [a, b] };
      }
      lines = best.lines;
    }
    const size = Math.min(max, ...lines.map(l => max * maxW / Math.max(1, width(l, max))));
    return { lines, size };
  }

  /* an ink coin. turn: the flip angle (0 face-on; the face shown flips at π/2); face: the side up at turn 0 */
  function coin(x, y, r, o = {}) {
    const turn = o.turn || 0, c = Math.cos(turn), sy = Math.max(0.06, Math.abs(c));
    const face = c >= 0 ? (o.face || 'H') : (o.face === 'T' ? 'H' : 'T');
    const ink = NB.PAL.ink;
    g.save(); g.translate(x, y); g.rotate(o.rot || 0);
    // the rim: a thin ink band below the face when tilted (the coin's thickness)
    const th = r * 0.12 * Math.sqrt(1 - sy * sy);
    if (th > 0.5) { g.beginPath(); g.ellipse(0, th, r, r * sy, 0, 0, Math.PI); g.lineTo(-r, 0); g.ellipse(0, 0, r, r * sy, 0, Math.PI, 0, true); g.closePath(); g.fillStyle = '#c9ccd4'; g.fill(); g.lineWidth = 4; g.strokeStyle = ink; g.stroke(); }
    g.scale(1, sy);
    g.beginPath(); g.arc(0, 0, r, 0, TAU); g.fillStyle = '#f3f1e6'; g.fill();
    g.lineWidth = 6 / Math.max(0.35, sy); g.strokeStyle = ink; g.stroke();
    g.beginPath(); g.arc(0, 0, r * 0.8, 0, TAU); g.lineWidth = 2.5 / Math.max(0.35, sy); g.stroke();
    if (sy > 0.2) { g.font = `700 ${Math.round(r * 1.05)}px ${HANDF}`; g.fillStyle = ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(face, 0, r * 0.06); }
    g.restore();
  }

  /* the flip on the title: up and down along a pencil arc, turning; pure in time */
  function flip(t0) {
    const bx = 470, by = 600, top = 250, per = 2.4;
    // the pencil arc (dashes) from the hand position up to the top of the flight
    const a = P(T, t0, t0 + 0.6);
    if (a > 0) {
      g.save(); g.strokeStyle = NB.PAL.pencil; g.lineWidth = 3; g.setLineDash([10, 12]); g.globalAlpha *= 0.8 * a;
      g.beginPath(); g.moveTo(bx - 150, by + 30); g.quadraticCurveTo(bx - 110, top - 40, bx, top + 40); g.stroke(); g.restore();
    }
    const u = ((T - t0) % per + per) % per / per;              // 0..1 through one flip
    const h = 4 * u * (1 - u);                                  // up and down (a parabola in time)
    const y = by - (by - top) * h - 40;
    coin(bx, y, 92, { turn: (T - t0) * 7.5, face: 'H', rot: -0.1 });
    // motion lines beside the coin
    g.save(); g.strokeStyle = NB.PAL.pencil; g.lineWidth = 4; g.lineCap = 'round';
    for (let k = -1; k <= 1; k++) { g.beginPath(); g.moveTo(bx + 130, y + k * 34); g.lineTo(bx + 170 + (k === 0 ? 20 : 0), y + k * 34); g.stroke(); }
    g.restore();
  }

  /* the tally: H H H H H (s1's five heads) writing on, a pencil tally, and a red "?" */
  function tally(t0) {
    const x0 = 250, y = 830, gap = 86;
    NB.write('FLIPS', x0 - 10, y - 86, { font: 'ui', weight: 700, size: 28, color: NB.PAL.pencil, t0 });
    for (let i = 0; i < 5; i++) {
      const at = t0 + 0.3 + i * 0.28;
      if (T < at) continue;
      g.save(); g.strokeStyle = NB.PAL.ink; g.lineWidth = 3; g.strokeRect(x0 + i * gap - 34, y - 38, 68, 76); g.restore();
      NB.write('H', x0 + i * gap, y + 2, { size: 60, weight: 700, align: 'center', t0: at, dur: 0.2 });
    }
    // the tally marks (4 strokes + the gate), pencil
    const tx = x0 + 5 * gap + 10, ta = t0 + 1.9;
    for (let i = 0; i < 4; i++) NB.pen([[tx + i * 20, y - 34], [tx + i * 20 - 4, y + 34]], { w: 5, color: NB.PAL.pencil, prog: E.outQuad(P(T, ta + i * 0.08, ta + i * 0.08 + 0.12)), step: 8 });
    NB.pen([[tx - 16, y + 22], [tx + 80, y - 22]], { w: 5, color: NB.PAL.pencil, prog: E.outQuad(P(T, ta + 0.4, ta + 0.55)), step: 8 });
    // what comes next? (red pen)
    NB.write('next: ?', x0 + 2 * gap, y + 94, { size: 46, weight: 700, color: NB.PAL.redpen, align: 'center', t0: ta + 0.7, rot: -0.03 });
  }

  class Title extends Scene {
    enter() { this.started = false; }
    go() {
      if (this.started) return;
      this.started = true;
      Audio.unlock();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
      Game.replace(new Levels.map());
    }
    keyDown(k) { if (k === 'Enter' || k === ' ') this.go(); }

    draw() {
      UI.allLessons();                       // "← All lessons" (a tab; in the portrait dock a button in its bar)
      const t = this.t, t0 = this.enteredAt, L = Game.lesson || {};
      NB.ground();
      tally(t0 + 0.2);
      flip(t0 + 0.3);
      // Jay rises in from below on the right, under the title
      const rise = E.outBack(P(T, t0 + 0.6, t0 + 1.1), 1.2);
      Puppet.draw('jay', 1790, 1080 + (1 - rise) * 600, 450, { facing: 0, t: T });
      // the title, handwritten; the highlighter lands on its last word ("Due!")
      const { lines, size } = titleLines(L.title || 'New lesson', 880, 118);
      let y = lines.length > 1 ? 250 : 320;
      lines.forEach((l, i) => {
        const last = i === lines.length - 1 ? l.split(/\s+/).pop() : null;
        NB.title(l, 1400, y, { size, t0: t0 + 0.3 + i * 0.5, hi: last });
        y += size * 1.2;
      });
      NB.write(L.subtitle || '', 1400, y + 20, { font: 'ui', size: 36, color: NB.PAL.pencil, align: 'center', t0: t0 + 1.2, dur: 0.4 });

      // portrait phone: the controls are in the dock (core/dock.js Dock.title); the stage keeps only the art
      if (UI.docked()) { Dock.title({ onStart: () => this.go() }); return; }
      if (t > 1.4 && UI.button('play', '▶ Play', PLAY.x, PLAY.y, { w: 420, h: 136, size: 60, primary: true })) this.go();
      if (t > 2.4) NB.write(UI.hint('(or press Enter)', 'tap ▶ Play to start'), PLAY.x, PLAY.y + 104 + (UI.tap(136) - 136) / 2, { font: 'ui', size: UI.text(30), color: NB.PAL.pencil, align: 'center', t0: t0 + 2.4 });
    }
  }
  Title.PLAY = PLAY;                         // tools/integration.js clicks it
  Title.coin = coin;

  /* the loading page (no assets yet): a pencil drawing a line along a ruler */
  Title.loading = function () { NB.loadingScreen('loading…'); };

  Levels.title = Title;
})();
